// lib/suggestions/events.ts
//
// Contrato de la telemetría de sugerencias (PR 3), compartido por el cliente
// (lib/suggestions/telemetry.ts) y la ruta de ingesta
// (app/api/kiosk/suggestion-events). Los mismos nombres y topes viven en los
// CHECK de la tabla suggestion_events; events.test.ts falla si se separan.
//
// Tres eventos, anónimos:
//   impression — la tarjeta entró al carrusel de esta sesión (una vez por
//                ítem y motivo: el motivo de un ítem cambia con el carrito).
//   tap        — el cliente tocó la tarjeta.
//   order      — ENLACE entre la sesión y un pedido, no una venta: se escribe
//                apenas el pedido existe (aunque no se haya cobrado). Venta es
//                solo lo que cuenta suggestion_report (pedidos cobrados): un
//                tap "vendió" si su ítem está en un pedido cobrado enlazado a
//                la misma sesión. No hay evento "added": la verdad es el pedido.
import type { SuggestionReason } from './types'

export const SUGGESTION_EVENT_NAMES = ['impression', 'tap', 'order'] as const
export type SuggestionEventName = (typeof SUGGESTION_EVENT_NAMES)[number]

/** Canales que admite la tabla (CHECK de `channel`). Quién escribe cada uno:
 *  kiosk → app/api/kiosk/suggestion-events; online → app/api/online/suggestion-events;
 *  display y pos → nadie todavía (panel pasivo / sin carrusel). */
export const SUGGESTION_EVENT_CHANNELS = ['kiosk', 'online', 'pos', 'display'] as const
export type SuggestionEventChannel = (typeof SUGGESTION_EVENT_CHANNELS)[number]

/** Momento en que el cliente vio la tarjeta (CHECK de `moment`, migración
 *  20261008090000). cart_review = carrusel del carrito (y, por default, la
 *  hoja de la tienda online, que tiene un solo momento); item_added = hoja del
 *  kiosko al agregar un plato (PR 6). */
export const SUGGESTION_MOMENTS = ['cart_review', 'item_added'] as const
export type SuggestionMoment = (typeof SUGGESTION_MOMENTS)[number]

/**
 * Forma del id de sesión: 8 a 64 caracteres de [A-Za-z0-9_-]. El cliente usa
 * crypto.randomUUID() o el fallback `mk-<base36>-<base36>`, los dos dentro de
 * este alfabeto. La ruta lo valida con esto y la tabla con el mismo patrón en
 * su CHECK (events.test.ts compara `.source` con la migración).
 */
export const SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/

/** Tope de eventos por lote; la ruta rechaza lotes mayores. */
export const MAX_EVENTS_PER_BATCH = 50
/** Posición máxima de una tarjeta en el carrusel (0 = primera). */
export const MAX_CARD_INDEX = 50
/** Puntaje máximo aceptado; el motor hoy no pasa de unos cientos. */
export const MAX_SCORE = 100000

export type SuggestionCardEvent = {
  event: 'impression' | 'tap'
  item_id: string
  reason: SuggestionReason
  /** Posición de la tarjeta en el carrusel, 0 = primera. */
  card_index: number
  score: number
  /** Ausente = 'cart_review'. Solo se manda cuando es 'item_added'. */
  moment?: SuggestionMoment
}

export type SuggestionOrderEvent = {
  event: 'order'
  order_id: string
}

export type SuggestionEvent = SuggestionCardEvent | SuggestionOrderEvent

export type SuggestionEventBatch = {
  session_id: string
  location_id: string
  events: SuggestionEvent[]
}
